import React, { useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { brands } from '../data/brands';
import { services } from '../data/services';
import { capitalizeWords } from '../utils/stringUtils';
import ServiceCenterHero from '../components/services/ServiceCenterHero';
import ServiceCenterDetails from '../components/services/ServiceCenterDetails';
import { generateMetadata } from '../modules/seo/generateMetadata';
import { ServiceSchema } from '../modules/seo/SchemaGenerator';

export default function ServiceCenter() {
  const { service: serviceId, state, city, area, brand: brandId } = useParams();
  const [selectedState, setSelectedState] = useState(state || '');
  const [selectedCity, setSelectedCity] = useState(city || '');
  const [selectedArea, setSelectedArea] = useState(area || '');

  const service = services.find(s => s.id === serviceId);
  const brand = brandId ? brands.find(b => b.id === brandId) : undefined;

  if (!service) {
    return <Navigate to="/services" replace />;
  }

  const location = {
    state: selectedState && capitalizeWords(selectedState.replace(/-/g, ' ')),
    city: selectedCity && capitalizeWords(selectedCity.replace(/-/g, ' ')),
    area: selectedArea && capitalizeWords(selectedArea.replace(/-/g, ' '))
  };

  // Generate title based on location depth
  let title = `${brand?.name ? `${brand.name} ` : ''}${service.name} Service Center`;
  if (location.state) {
    title += ` in ${location.state}`;
    if (location.city) {
      title = `${brand?.name ? `${brand.name} ` : ''}${service.name} Service Center in ${location.city}, ${location.state}`;
      if (location.area) {
        title = `${brand?.name ? `${brand.name} ` : ''}${service.name} Service Center in ${location.area}, ${location.city}`;
      }
    }
  }

  const metadata = generateMetadata({
    service,
    city: location.city,
    state: location.state,
    brand: brand?.name
  });

  const schema = ServiceSchema({
    name: title,
    description: service.description,
    location: location.state && location.city ? { city: location.city, state: location.state } : undefined,
    price: { min: 499, max: 1499 }
  });

  return (
    <>
      <Helmet>
        <title>{title} | Parakh Services</title>
        <meta name="description" content={metadata.description} />
        <meta name="keywords" content={metadata.keywords.join(', ')} />
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      </Helmet>

      <ServiceCenterHero 
        service={service}
        brandName={brand?.name}
        location={location}
      />

      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ServiceCenterDetails 
            service={service}
            brandName={brand?.name}
            location={location}
          />
        </div>
      </div>
    </>
  );
}